import { useState } from "react";
import { Button } from "@/components/ui/button";
import { isAdminOrAbove } from '@/lib/permissions';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Layout } from "@/components/common/Layout/Layout";
import { PageBody, PageHeader, PageShell } from "@/components/ui/page-shell";
import { useAuth } from "@/hooks/useAuth";
import { useHideAmounts } from "@/hooks/useHideAmounts";
import { useToast } from "@/hooks/use-toast";
import { ROUTES } from "@/constants/routes";
import api from "@/lib/api";
import { ArrowRight, BarChart3, TableIcon, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export function Settings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    re_new_password: "",
  });

  const userGroups = user?.groups || [];
  const isAdmin = isAdminOrAbove(userGroups);

  const prefs = (user?.preferences ?? {}) as Record<string, any>;
  const [preferTable, setPreferTable] = useState<boolean>(!!prefs.prefer_table_view);
  const [prefSaving, setPrefSaving] = useState(false);
  const { hideAmounts, toggleHideAmounts, saving: hideSaving } = useHideAmounts();

  const DATE_FORMAT_OPTIONS = [
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
    "DD.MM.YYYY",
    "DD-MM-YYYY",
  ] as const;
  const [dateFormat, setDateFormat] = useState<string>(
    (user as any)?.date_format || "DD/MM/YYYY",
  );
  const [dateFormatSaving, setDateFormatSaving] = useState(false);

  const handleDateFormatChange = async (value: string) => {
    const previous = dateFormat;
    setDateFormat(value);
    setDateFormatSaving(true);
    try {
      await api.patch(`${ROUTES.API.USERS}me/`, { date_format: value });
      await useAuth.getState().checkAuth();
      toast({ title: "Saved", description: `Dates will show as ${value}.` });
    } catch {
      setDateFormat(previous);
      toast({
        title: "Error",
        description: "Failed to save date format",
        variant: "destructive",
      });
    } finally {
      setDateFormatSaving(false);
    }
  };

  const togglePreferTable = async (value: boolean) => {
    setPreferTable(value);
    setPrefSaving(true);
    try {
      await api.patch(`${ROUTES.API.USERS}update_preferences/`, {
        preferences: { prefer_table_view: value },
      });
      // Re-fetch current user so preference is reflected everywhere
      await useAuth.getState().checkAuth();
      toast({
        title: "Saved",
        description: value
          ? "Statistics will now show tables instead of graphs"
          : "Statistics will now show graphs",
      });
    } catch {
      setPreferTable(!value);
      toast({ title: "Error", description: "Failed to save preference", variant: "destructive" });
    } finally {
      setPrefSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate passwords match
      if (passwordData.new_password !== passwordData.re_new_password) {
        toast({
          title: "Error",
          description: "New passwords do not match",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Submit password change
      await api.post(`${ROUTES.API.USERS}change_password/`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
        re_new_password: passwordData.re_new_password,
      });

      toast({
        title: "Success",
        description: "Password changed successfully",
      });

      // Reset form
      setPasswordData({
        current_password: "",
        new_password: "",
        re_new_password: "",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to change password. Please check your current password.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Settings"
          description="Your account preferences and firm-wide configuration."
        />
        <PageBody>

        {/* Admin Quick Access */}
        {isAdmin && (
          <Card>
            <CardContent className="flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-semibold">Firm Administration</div>
                <p className="text-sm text-muted-foreground">
                  Configure firm-wide settings, document templates, and integrations
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate(ROUTES.FIRM_SETTINGS)}
                className="gap-2 shrink-0"
              >
                Manage firm settings
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* User Settings */}
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Your preferences</h2>
            <p className="text-sm text-muted-foreground">Personal settings for your account</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Display Preferences</CardTitle>
              <CardDescription>
                Choose how statistics and comparisons are displayed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {preferTable ? (
                    <TableIcon className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <BarChart3 className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <Label htmlFor="prefer-table" className="text-sm font-medium">
                      Prefer tables over graphs
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      When enabled, comparison data will be shown as detailed number tables instead of line charts
                    </p>
                  </div>
                </div>
                <Switch
                  id="prefer-table"
                  checked={preferTable}
                  onCheckedChange={togglePreferTable}
                  disabled={prefSaving}
                />
              </div>

              {isAdmin && (
                <div className="flex items-center justify-between border-t pt-6">
                  <div className="flex items-center gap-3">
                    <EyeOff className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <Label htmlFor="hide-amounts" className="text-sm font-medium">
                        Hide euro amounts
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        When enabled, all euro amounts across the entire application will be hidden. Useful for presentations and screen sharing.
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="hide-amounts"
                    checked={hideAmounts}
                    onCheckedChange={toggleHideAmounts}
                    disabled={hideSaving}
                  />
                </div>
              )}

              <div className="flex items-center justify-between border-t pt-6">
                <div>
                  <Label htmlFor="date-format" className="text-sm font-medium">
                    Date format
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Applied everywhere dates are rendered (tables, reports, date pickers).
                  </p>
                </div>
                <select
                  id="date-format"
                  className="h-9 rounded-md border border-input bg-card px-3 text-sm shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
                  value={dateFormat}
                  onChange={(e) => handleDateFormatChange(e.target.value)}
                  disabled={dateFormatSaving}
                >
                  {DATE_FORMAT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>
                Update your account password
              </CardDescription>
            </CardHeader>
            <form onSubmit={handlePasswordChange}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        current_password: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        new_password: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="re_new_password">Confirm New Password</Label>
                  <Input
                    id="re_new_password"
                    type="password"
                    value={passwordData.re_new_password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        re_new_password: e.target.value,
                      })
                    }
                    required
                  />
                </div>
                <div className="text-sm">
                  <Link to={ROUTES.FORGOT_PASSWORD} className="text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Updating..." : "Change Password"}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
        </PageBody>
      </PageShell>
    </Layout>
  );
}